import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import {
  AbsenceApprovalCard,
  DayViolationCard,
} from "@/pages/admin/zeiterfassung/PendingApprovalCards";
import {
  usePendingApprovals,
  type DayViolationFilter,
} from "@/pages/admin/zeiterfassung/usePendingApprovals";

interface ApprovalsPanelProps {
  refreshKey: number;
  onChanged: () => void;
}

const DAY_FILTERS: { value: DayViolationFilter; label: string }[] = [
  { value: "all", label: "Alle Verstöße" },
  { value: "pending", label: "Begründung ausstehend" },
  { value: "justified", label: "Begründung vorhanden" },
  { value: "historical", label: "Historische GF-Daten" },
];

export function ApprovalsPanel({ refreshKey, onChanged }: ApprovalsPanelProps) {
  const {
    dayItems,
    absenceItems,
    loading,
    busyAbsenceId,
    absenceComments,
    setAbsenceComment,
    reviewAbsence,
    acknowledgeAbsence,
    totalPending,
    dayStatusFilter,
    setDayStatusFilter,
    dayUserFilter,
    setDayUserFilter,
    dayDateFrom,
    setDayDateFrom,
    dayDateTo,
    setDayDateTo,
    applyDayFilters,
  } = usePendingApprovals({ refreshKey, onChanged });

  return (
    <div className="space-y-6">
      <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
        <h2 className="text-lg font-semibold text-label">Freigaben & Arbeitszeitverstöße</h2>
        <p className="mt-1 text-sm text-label-secondary">
          Abwesenheitsanträge werden hier freigegeben. Arbeitszeitverstöße sind nur noch einsehbar
          – eine Freigabe durch die Geschäftsführung ist nicht mehr möglich. Stattdessen hinterlegt
          der Mitarbeiter eine Begründung.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : totalPending === 0 && dayItems.length === 0 ? (
        <Alert variant="success" title="Keine offenen Einträge">
          Keine ausstehenden Abwesenheitsfreigaben und keine angezeigten Arbeitszeitverstöße.
        </Alert>
      ) : null}

      {!loading && absenceItems.length > 0 ? (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-label">
            Abwesenheitsanträge ({absenceItems.length})
          </h3>
          {absenceItems.map((item) => (
            <AbsenceApprovalCard
              key={item.id}
              item={item}
              comment={absenceComments[item.id] ?? ""}
              onCommentChange={(value) => setAbsenceComment(item.id, value)}
              busy={busyAbsenceId === item.id}
              onApprove={() => void reviewAbsence(item.id, "approve")}
              onReject={() => void reviewAbsence(item.id, "reject")}
              onAcknowledge={() => void acknowledgeAbsence(item.id)}
            />
          ))}
        </section>
      ) : null}

      {!loading ? (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-label">
            Arbeitszeitverstöße ({dayItems.length})
          </h3>

          <div className="flex flex-wrap items-end gap-3 rounded-ios border border-separator bg-card p-3 shadow-ios">
            <label className="space-y-1">
              <span className="eh-label">Status</span>
              <select
                className="eh-input"
                value={dayStatusFilter}
                onChange={(e) => setDayStatusFilter(e.target.value as DayViolationFilter)}
              >
                {DAY_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="eh-label">Mitarbeiter (Login)</span>
              <input
                type="text"
                className="eh-input"
                value={dayUserFilter}
                onChange={(e) => setDayUserFilter(e.target.value)}
                placeholder="z. B. mfrank"
              />
            </label>
            <label className="space-y-1">
              <span className="eh-label">Von</span>
              <input
                type="date"
                className="eh-input"
                value={dayDateFrom}
                onChange={(e) => setDayDateFrom(e.target.value)}
              />
            </label>
            <label className="space-y-1">
              <span className="eh-label">Bis</span>
              <input
                type="date"
                className="eh-input"
                value={dayDateTo}
                onChange={(e) => setDayDateTo(e.target.value)}
              />
            </label>
            <Button variant="secondary" size="sm" onClick={() => applyDayFilters()}>
              Filtern
            </Button>
          </div>

          {dayItems.length === 0 ? (
            <Alert variant="success" title="Keine Arbeitszeitverstöße">
              Für die gewählten Filter sind keine Verstöße vorhanden.
            </Alert>
          ) : (
            dayItems.map((item) => <DayViolationCard key={item.id} item={item} />)
          )}
        </section>
      ) : null}
    </div>
  );
}
